import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type { PayoutDestination, Bank, ResolveAccountResult, CreatePayoutDestinationPayload } from '../types';

export const payoutDestinationsApi = {
  /**
   * Returns null when the merchant has no destination yet. osprey-api returns an
   * `{ exists, destination }` envelope (NOT the destination directly) — when none is
   * registered it's `{ exists: false, destination: null }`. We must extract
   * `.destination` so callers get a PayoutDestination | null; passing the raw envelope
   * through made MerchantPayoutDestination read undefined fields (destinationType,
   * businessName…) and throw. A bare destination is tolerated for forward-compat.
   */
  get: async (tenantId: string): Promise<PayoutDestination | null> => {
    try {
      const response = await ApiFetch.get<
        Enveloped<{ exists: boolean; destination: PayoutDestination | null } | PayoutDestination>
      >(`/payout-destinations/${tenantId}`);
      const data = unwrap(response);
      if (data && typeof data === 'object' && 'exists' in data) {
        return (data as { exists: boolean; destination: PayoutDestination | null }).destination ?? null;
      }
      return (data as PayoutDestination) ?? null;
    } catch {
      // No registered destination (502/404) → treat as "not onboarded yet".
      return null;
    }
  },

  listBanks: async (): Promise<Bank[]> => {
    const response = await ApiFetch.get<Enveloped<Bank[]>>('/payout-destinations/banks');
    return unwrap(response);
  },

  resolveAccount: async (accountNumber: string, bankCode: string): Promise<ResolveAccountResult> => {
    const response = await ApiFetch.post<Enveloped<ResolveAccountResult>>('/payout-destinations/resolve-account', {
      accountNumber,
      bankCode,
    });
    return unwrap(response);
  },

  create: async (data: CreatePayoutDestinationPayload): Promise<PayoutDestination> => {
    const response = await ApiFetch.post<Enveloped<PayoutDestination>>('/payout-destinations', data);
    return unwrap(response);
  },
};
